/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Scheduling API - internal scheduling, appointments/events, public
 * booking tokens.
 *
 * Mirrors backend/scheduling/urls.py, mounted at /api/v1/scheduling/.
 * All endpoints tenant-scoped; writes require Staff-or-above server-side.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type {
  Appointment,
  Attendance,
  Booking,
  BookingToken,
  GenerateShiftsResult,
  Resource,
  EventMetrics,
  SchedEvent,
  ScheduleTemplate,
  Shift,
} from "../model/types";

const DESK_INVALIDATION = { type: "CheckIn" as const, id: "DESK" };

/** RTK Query endpoints for the scheduling domain. */
export const schedulingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // --- Resources ---
    deleteResource: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/scheduling/resources/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Resource", id: "LIST" }],
    }),
    listResources: build.query<Resource[], void>({
      query: () => ({ url: "/scheduling/resources/" }),
      transformResponse: (r: { results?: Resource[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Resource", id: "LIST" }],
    }),
    createResource: build.mutation<Resource, Partial<Resource>>({
      query: (body) => ({ url: "/scheduling/resources/", method: "POST", body }),
      invalidatesTags: [{ type: "Resource", id: "LIST" }],
    }),
    updateResource: build.mutation<Resource, { id: number } & Partial<Resource>>({
      query: ({ id, ...body }) => ({
        url: `/scheduling/resources/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Resource", id: "LIST" }],
    }),

    // --- Schedule templates ---
    listTemplates: build.query<ScheduleTemplate[], { program?: number } | void>({
      query: (params) => ({
        url: "/scheduling/templates/",
        params: params ?? {},
      }),
      transformResponse: (r: { results?: ScheduleTemplate[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "ScheduleTemplate", id: "LIST" }],
    }),
    createTemplate: build.mutation<ScheduleTemplate, Partial<ScheduleTemplate>>({
      query: (body) => ({ url: "/scheduling/templates/", method: "POST", body }),
      invalidatesTags: [
        { type: "ScheduleTemplate", id: "LIST" },
        // The week's pattern changed, so the calendar behind it is stale.
        { type: "Shift", id: "LIST" },
      ],
    }),
    updateTemplate: build.mutation<
      ScheduleTemplate,
      { id: number; data: Partial<ScheduleTemplate> }
    >({
      query: ({ id, data }) => ({
        url: `/scheduling/templates/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        { type: "ScheduleTemplate", id: "LIST" },
        // Editing the pattern does not retime shifts already generated from
        // it, but the list showing both must be refetched together.
        { type: "Shift", id: "LIST" },
      ],
    }),
    deleteTemplate: build.mutation<void, number>({
      query: (id) => ({
        url: `/scheduling/templates/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "ScheduleTemplate", id: "LIST" },
        { type: "Shift", id: "LIST" },
      ],
    }),
    generateShifts: build.mutation<
      GenerateShiftsResult,
      { id: number; date_from: string; date_to: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/scheduling/templates/${id}/generate-shifts/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Shift", id: "LIST" }, DESK_INVALIDATION],
    }),

    // --- Shifts ---
    listShifts: build.query<
      Shift[],
      {
        starts_after?: string;
        starts_before?: string;
        program?: number;
        /** Comma-separated PersonRole ids (member profile roster). */
        person_role?: string;
        page_size?: number;
        /** "1" scopes the result to the signed-in person's own shifts. */
        mine?: string;
      } | void
    >({
      query: (params) => ({
        url: "/scheduling/shifts/",
        params: { page_size: 500, ...(params ?? {}) },
      }),
      transformResponse: (r: { results?: Shift[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Shift", id: "LIST" }],
    }),
    createShift: build.mutation<Shift, Partial<Shift>>({
      query: (body) => ({ url: "/scheduling/shifts/", method: "POST", body }),
      invalidatesTags: [{ type: "Shift", id: "LIST" }, DESK_INVALIDATION],
    }),
    assignShift: build.mutation<Shift, { id: number; person_role: number | null }>({
      query: ({ id, person_role }) => ({
        url: `/scheduling/shifts/${id}/assign/`,
        method: "POST",
        body: { person_role },
      }),
      invalidatesTags: [{ type: "Shift", id: "LIST" }, DESK_INVALIDATION],
    }),
    signUpShift: build.mutation<{ detail: string; shift_id: number }, number>({
      query: (id) => ({
        url: `/scheduling/shifts/${id}/sign-up/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Shift", id: "LIST" }, DESK_INVALIDATION],
    }),
    dropShift: build.mutation<Shift, number>({
      query: (id) => ({
        url: `/scheduling/shifts/${id}/drop/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Shift", id: "LIST" }, DESK_INVALIDATION],
    }),

    // --- Bookings ---
    // Bookings are read per facility (`?resource=`) and, like every other
    // list here, need an explicit page_size - the DRF default of 10 would
    // quietly hide a busy room's diary.
    listBookings: build.query<Booking[], { resource?: number } | void>({
      query: (params) => ({
        url: "/scheduling/bookings/",
        params: { page_size: 500, ...(params ?? {}) },
      }),
      transformResponse: (r: { results?: Booking[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Booking", id: "LIST" }],
    }),
    createBooking: build.mutation<Booking, Partial<Booking>>({
      query: (body) => ({ url: "/scheduling/bookings/", method: "POST", body }),
      invalidatesTags: [{ type: "Booking", id: "LIST" }],
    }),
    deleteBooking: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/scheduling/bookings/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Booking", id: "LIST" }],
    }),

    // --- Appointments ---
    //
    // v4.0.1 fix: the transformResponse was previously INSIDE the query()
    // return object - ``query: (params) => ({ transformResponse: ..., url, params })``.
    // RTK Query silently ignored it because transformResponse is not a valid
    // property of the query config object - it's a sibling of query at the
    // endpoint definition level. Result: the API returned the paginated
    // envelope ``{ count, results: [...] }`` and RTK Query set ``data`` to
    // the entire envelope. The Appointments tab rendered nothing because
    // ``.map()`` on a plain object produces no output.
    //
    // Correct structure:
    //   endpoint({
    //     query: () => ({ url, params }),        ← returns fetch config only
    //     transformResponse: (r) => r.results,   ← sibling of query, not inside it
    //     providesTags: [...],
    //   })
    listAppointments: build.query<
      Appointment[],
      { starts_after?: string; starts_before?: string } | void
    >({
      query: (params) => ({
        url: "/scheduling/appointments/",
        params: params ?? {},
      }),
      // Extract the results array from the DRF paginated envelope.
      // Without this, data = { count, page, results: [...] } and
      // Array.isArray(data) is false, so .map() produces nothing.
      transformResponse: (r: { results?: Appointment[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Appointment", id: "LIST" }],
    }),
    createAppointment: build.mutation<Appointment, Partial<Appointment>>({
      query: (body) => ({
        url: "/scheduling/appointments/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Appointment", id: "LIST" }],
    }),
    rescheduleAppointment: build.mutation<
      Appointment,
      { id: number; starts_at: string; ends_at: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/scheduling/appointments/${id}/reschedule/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Appointment", id: "LIST" }],
    }),

    // --- Events ---
    // page_size: DRF defaults to 10, which silently truncated the events
    // list (memphis-rox already has 11). Same truncation class as the
    // shifts board fix - ask for the whole list, group and filter client-side.
    listEvents: build.query<SchedEvent[], void>({
      query: () => ({ url: "/scheduling/events/", params: { page_size: 500 } }),
      transformResponse: (r: { results?: SchedEvent[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "SchedEvent", id: "LIST" }],
    }),
    listEventsPage: build.query<
      { results: SchedEvent[]; count: number },
      { page?: number; page_size?: number; search?: string }
    >({
      query: ({ page = 1, page_size = 20, search }) => {
        const params: Record<string, string | number> = { page, page_size };
        if (search?.trim()) params.search = search.trim();
        return { url: "/scheduling/events/", params };
      },
      transformResponse: (r: { results?: SchedEvent[]; count?: number }) => ({
        results: Array.isArray(r?.results) ? r.results : [],
        count: typeof r?.count === "number" ? r.count : 0,
      }),
    }),
    getEvent: build.query<SchedEvent, number>({
      query: (id) => ({ url: `/scheduling/events/${id}/` }),
      providesTags: (_r, _e, id) => [{ type: "SchedEvent", id }],
    }),
    createEvent: build.mutation<SchedEvent, Partial<SchedEvent>>({
      query: (body) => ({ url: "/scheduling/events/", method: "POST", body }),
      invalidatesTags: [{ type: "SchedEvent", id: "LIST" }],
    }),
    inviteToEvent: build.mutation<
      Attendance[],
      {
        eventId: number;
        person_ids?: number[];
        email_invites?: { email: string; name?: string }[];
      }
    >({
      query: ({ eventId, ...body }) => ({
        url: `/scheduling/events/${eventId}/invite/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Attendance", id: "LIST" }],
    }),
    rsvpToEvent: build.mutation<
      Attendance,
      { eventId: number; status: "rsvp_yes" | "rsvp_no" }
    >({
      query: ({ eventId, status }) => ({
        url: `/scheduling/events/${eventId}/rsvp/`,
        method: "POST",
        body: { status },
      }),
      invalidatesTags: [
        { type: "SchedEvent", id: "LIST" },
        { type: "Attendance", id: "LIST" },
      ],
    }),

    // v3.8 L2.C1 - Event update + delete
    updateEvent: build.mutation<SchedEvent, { id: number; data: Partial<SchedEvent> }>({
      query: ({ id, data }) => ({
        url: `/scheduling/events/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SchedEvent", id },
        { type: "SchedEvent", id: "LIST" },
      ],
    }),
    deleteEvent: build.mutation<void, number>({
      query: (id) => ({ url: `/scheduling/events/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "SchedEvent", id: "LIST" }],
    }),

    // v3.8 L2.C1 - Shift update + delete
    updateShift: build.mutation<Shift, { id: number; data: Partial<Shift> }>({
      query: ({ id, data }) => ({
        url: `/scheduling/shifts/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Shift", id },
        { type: "Shift", id: "LIST" },
      ],
    }),
    deleteShift: build.mutation<void, number>({
      query: (id) => ({ url: `/scheduling/shifts/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "Shift", id: "LIST" }, DESK_INVALIDATION],
    }),

    // v3.8 L2.C1 - Appointment update + delete
    updateAppointment: build.mutation<Appointment, { id: number; data: Partial<Appointment> }>({
      query: ({ id, data }) => ({
        url: `/scheduling/appointments/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Appointment", id },
        { type: "Appointment", id: "LIST" },
      ],
    }),
    deleteAppointment: build.mutation<void, number>({
      query: (id) => ({ url: `/scheduling/appointments/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "Appointment", id: "LIST" }],
    }),

    // --- Attendance ---
    // transformResponse used to sit INSIDE the query object here, where RTK
    // never reads it - the same defect that left the shift attendance panel
    // permanently empty. It belongs as a sibling of `query`.
    listAttendance: build.query<
      Attendance[],
      { event?: number; booking_token?: number; page_size?: number } | void
    >({
      query: (params) => ({
        url: "/scheduling/attendance/",
        params: { page_size: 500, ...(params ?? {}) },
      }),
      transformResponse: (r: { results?: Attendance[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Attendance", id: "LIST" }],
    }),
    createAttendance: build.mutation<Attendance, Partial<Attendance>>({
      query: (body) => ({
        url: "/scheduling/attendance/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Attendance", id: "LIST" }],
    }),
    checkInAttendance: build.mutation<Attendance, { id: number }>({
      query: ({ id }) => ({
        url: `/scheduling/attendance/${id}/check-in/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Attendance", id: "LIST" }],
    }),
    deleteAttendance: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/scheduling/attendance/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Attendance", id: "LIST" }],
    }),
    /**
     * NOT DEPLOYED. `events/:id/walk-in/` and `events/:id/broadcast/` exist
     * only in the local backend branch; staging returns 404 for both.
     * EventsPage therefore records a walk-in as create-attendance followed by
     * check-in, which is the same end state on endpoints that are live today.
     * Leave these here for when the backend patch lands.
     */
    addWalkIn: build.mutation<
      Attendance,
      { eventId: number; person_id?: number; name?: string; email?: string }
    >({
      query: ({ eventId, ...body }) => ({
        url: `/scheduling/events/${eventId}/walk-in/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Attendance", id: "LIST" }],
    }),
    broadcastToEvent: build.mutation<
      { sent: number; failed: { attendance: number; error: string }[] },
      {
        eventId: number;
        subject: string;
        body: string;
        recipients?: "all" | "unresponded" | "confirmed";
      }
    >({
      query: ({ eventId, ...body }) => ({
        url: `/scheduling/events/${eventId}/broadcast/`,
        method: "POST",
        body,
      }),
    }),

    // --- Booking tokens (staff-minted public links) ---
    listBookingTokens: build.query<BookingToken[], void>({
      query: () => ({
        url: "/scheduling/booking-tokens/",
        params: { page_size: 500 },
      }),
      transformResponse: (r: { results?: BookingToken[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "BookingToken", id: "LIST" }],
    }),
    createBookingToken: build.mutation<BookingToken, Partial<BookingToken>>({
      query: (body) => ({
        url: "/scheduling/booking-tokens/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "BookingToken", id: "LIST" }],
    }),
    revokeBookingToken: build.mutation<BookingToken, { id: number }>({
      query: ({ id }) => ({
        url: `/scheduling/booking-tokens/${id}/revoke/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "BookingToken", id: "LIST" }],
    }),

    getEventMetrics: build.query<EventMetrics | null, number>({
      queryFn: async (eventId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/scheduling/events/${eventId}/metrics/`,
        });
        if (result.error) {
          const status = (result.error as { status?: number }).status;
          if (status === 404) return { data: null };
          return { error: result.error };
        }
        return { data: result.data as EventMetrics };
      },
      providesTags: (_r, _e, eventId) => [
        { type: "EventMetrics", id: eventId },
      ],
    }),
    createEventMetrics: build.mutation<
      EventMetrics,
      { eventId: number; data: Partial<EventMetrics> }
    >({
      query: ({ eventId, data }) => ({
        url: `/scheduling/events/${eventId}/metrics/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "EventMetrics", id: eventId },
      ],
    }),
  }),
});

export const {
  useListResourcesQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  useDeleteResourceMutation,
  useListTemplatesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useGenerateShiftsMutation,
  useListShiftsQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useAssignShiftMutation,
  useSignUpShiftMutation,
  useDropShiftMutation,
  useListBookingsQuery,
  useCreateBookingMutation,
  useDeleteBookingMutation,
  useListAppointmentsQuery,
  useCreateAppointmentMutation,
  useUpdateAppointmentMutation,
  useDeleteAppointmentMutation,
  useRescheduleAppointmentMutation,
  useListEventsQuery,
  useLazyListEventsPageQuery,
  useGetEventQuery,
  useGetEventMetricsQuery,
  useCreateEventMutation,
  useInviteToEventMutation,
  useRsvpToEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useListAttendanceQuery,
  useCreateAttendanceMutation,
  useCheckInAttendanceMutation,
  useDeleteAttendanceMutation,
  useAddWalkInMutation,
  useBroadcastToEventMutation,
  useListBookingTokensQuery,
  useCreateBookingTokenMutation,
  useRevokeBookingTokenMutation,
  useCreateEventMetricsMutation,
} = schedulingApi;
